import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { SaveOutlined, DeleteOutline, Close } from "@mui/icons-material";
import { cloneDeep } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { baseUrl, dealerChannelsOptions } from "@/consts";
import { DealerMasterDealer } from "@/interfaces";
import { useDataApi } from "@/hooks";
import { ErrorMessage } from "@/components/atoms";
import { colors, sxButton } from "@/theme";
import {
  formatAxiosError,
  isNonEmptyArray,
  isNonEmptyString,
  isNumber,
  isStringEqualCI,
} from "@/utils";
import { YupHelper } from "@/helpers";

export type DealerEditDialogProps = {
  dealer?: DealerMasterDealer;
  open?: boolean;
  onClose?: (dealer?: DealerMasterDealer) => void;
  onDeleteRequest?: (dealer: DealerMasterDealer) => void;
};

type DealerEditFormValues = DealerMasterDealer & {
  posAllowance?: boolean;
  posAllowanceAmount?: string;
  posAvailableAllowance?: string;
  percentageOff?: number;
  notes?: string;
};

const validationSchema = Yup.object().shape({
  dealerIdentifier: Yup.string()
    .required("Bage ID is required")
    .test(
      "checkDupDealerIdentifier",
      "Bage ID already exists",
      (value, context) =>
        YupHelper.testDuplicate(
          value,
          context,
          `${baseUrl.DEALER}?search=`,
          "dealerId",
          "dealerIdentifier"
        )
    ),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  company: Yup.string().optional().nullable(),
  email: Yup.string()
    .required("Email is required")
    .test("checkDupEmail", "Email already exists", (value, context) =>
      YupHelper.testDuplicate(
        value,
        context,
        `${baseUrl.DEALER}?search=`,
        "dealerId",
        "email"
      )
    ),
  phone: Yup.string().optional().nullable(),
  masterDealerId: Yup.string().optional().nullable(),
  channel: Yup.string().optional().nullable(),
});

const initialValues: DealerEditFormValues = {
  dealerId: undefined as unknown as number,
  dealerIdentifier: undefined,
  firstName: undefined,
  lastName: undefined,
  company: undefined,
  email: undefined,
  phone: undefined,
  masterDealerId: undefined,
  channel: undefined,
  posAllowance: true,
  posAllowanceAmount: undefined,
  posAvailableAllowance: undefined,
  percentageOff: undefined,
  notes: '',
};

const mapDealerToFormValues = (dealer?: DealerMasterDealer): DealerEditFormValues => {
  console.log('Dealer Channels:', dealer?.channels);
  return {
    ...dealer,
    posAllowance: dealer?.acbAllowanceEnabled ?? true,
    posAllowanceAmount: dealer?.acbAllowance != null ? dealer.acbAllowance.toString() : '',
    posAvailableAllowance: dealer?.acbAllowance != null && dealer?.acbAllowanceUsed != null ? 
      (dealer.acbAllowance - dealer.acbAllowanceUsed).toString() : '',
    percentageOff: dealer?.percentageOff !== undefined ? dealer.percentageOff : undefined,
    notes: dealer?.notes ?? '',
  };
};

const DealerEditDialog: React.FC<DealerEditDialogProps> = ({
  dealer: _dealer,
  open = false,
  onClose,
  onDeleteRequest,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const { post, put, error } = useDataApi(baseUrl.DEALER_DEALER_MASTER_DEALER);

  const { errors, touched, handleBlur, ...formik } = useFormik<DealerEditFormValues>({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: cloneDeep(_dealer ? mapDealerToFormValues(_dealer) : initialValues),
    onSubmit(values, { validateForm }) {
      validateForm(values).then(() => onConfirmButtonClick(values));
    },
  });

  useEffect(() => {
    if (error?.config?.url?.indexOf(baseUrl.DEALER_DEALER_MASTER_DEALER) >= 0) {
      setErrorMessage(formatAxiosError(error));
    }
  }, [error]);

  const onConfirmButtonClick = async (dealer?: DealerEditFormValues) => {
    setErrorMessage(undefined);

    if (!dealer || !formik.dirty) {
      onClose?.();
      return;
    }

    let _error = undefined;

    // Map form values to API payload
    const apiPayload = {
      ...dealer,
      acbAllowanceEnabled: dealer.posAllowance ?? false,
      acbAllowance: dealer.posAllowanceAmount ? parseFloat(dealer.posAllowanceAmount.toString().replace(/[^\d.]/g, '')) : 0,
      // acbAllowanceUsed: dealer.posAvailableAllowance ? parseFloat(dealer.posAvailableAllowance.toString().replace(/[^\d.]/g, '')) : 0,
      percentageOff: dealer.percentageOff ? Number(dealer.percentageOff) : 0,
    };

    try {
      await validationSchema.validate(dealer);

      if (!isNumber(dealer.dealerId)) {
        await post?.(baseUrl.DEALER_DEALER_MASTER_DEALER, apiPayload);
      } else {
        await put?.(
          `${baseUrl.DEALER_DEALER_MASTER_DEALER}/${dealer.dealerId}`,
          apiPayload
        );
      }
    } catch (e) {
      _error = true;

      if (e instanceof Yup.ValidationError) {
        if (isNonEmptyArray(e.errors)) {
          setErrorMessage(e.errors[0]);
        } else {
          setErrorMessage("Unknown validation error");
          console.log(e);
        }
      } else {
        console.log(e);
      }
    }

    if (error || _error) {
      return;
    }

    onClose?.(dealer);
  };

  const handleClose = (_: any, reason: string) => {
    if (isStringEqualCI(reason, "backdropClick")) {
      return;
    }

    onConfirmButtonClick();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose} PaperProps={{
      sx: {
        borderRadius: 3,
        p: 0,
        overflow: 'visible',
      }
    }}>
      <Box sx={{ position: 'relative', p: 0, pt: 3, pb: 1, borderBottom: `1px solid ${colors.neutralGrey500}` }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 32,
            textAlign: 'center',
            letterSpacing: 0.5,
            color: '#2C3856',
          }}
        >
          {formik.values.dealerId ? 'Edit Attendee' : 'Add Attendee'}
        </Typography>
        <Button
          onClick={() => onClose?.()}
          sx={{
            minWidth: 0,
            p: 0,
            position: 'absolute',
            top: 16,
            right: 24,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#1563FF',
            color: '#fff',
            boxShadow: 1,
            '&:hover': { background: '#0039CB' },
            zIndex: 2,
          }}
        >
          <Close />
        </Button>
      </Box>
      <DialogContent sx={{ pt: 2, pb: 3, px: 0, overflow: 'visible' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateRows: 'repeat(5, auto)',
          gap: '20px',
          px: 3,
          pt: 1,
          pb: 0,
          alignItems: 'start',
        }}>
          {/* Row 1: First Name, Last Name, Badge ID */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr', gap: '20px', gridRow: '1' }}>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>First Name</Typography>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="Enter First Name"
                required
                value={formik.values.firstName}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("firstName", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.firstName && errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Last Name</Typography>
              <TextField
                id="lastName"
                name="lastName"
                placeholder="Enter Last Name"
                required
                value={formik.values.lastName}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("lastName", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.lastName && errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Badge ID</Typography>
              <TextField
                id="dealerIdentifier"
                name="dealerIdentifier"
                placeholder="Enter Badge ID"
                required
                value={formik.values.dealerIdentifier}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("dealerIdentifier", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.dealerIdentifier && errors.dealerIdentifier)}
                helperText={touched.dealerIdentifier && errors.dealerIdentifier}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
          </Box>

          {/* Row 2: Email, Phone */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '2.8fr 1.2fr', gap: '20px', gridRow: '2' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Email</Typography>
              <TextField
                id="email"
                name="email"
                placeholder="Enter Your Email"
                required
                value={formik.values.email}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("email", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Phone Number</Typography>
              <TextField
                id="phone"
                name="phone"
                placeholder="(212) 555-1212"
                value={formik.values.phone}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("phone", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.phone && errors.phone)}
                helperText={touched.phone && errors.phone}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
          </Box>

          {/* Row 3: Company, Master Dealer ID, Channel */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', gridRow: '3' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Company</Typography>
              <TextField
                id="company"
                name="company"
                placeholder="Enter Company"
                value={formik.values.company}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("company", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.company && errors.company)}
                helperText={touched.company && errors.company}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Master Dealer ID</Typography>
              <TextField
                id="masterDealerId"
                name="masterDealerId"
                placeholder="Master Dealer ID"
                value={formik.values.masterDealerId}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("masterDealerId", value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.masterDealerId && errors.masterDealerId)}
                helperText={touched.masterDealerId && errors.masterDealerId}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Channel</Typography>
              <Autocomplete
                options={dealerChannelsOptions}
                value={formik.values.channel || ""}
                onInputChange={(_, value) => {
                  formik.setFieldValue("channel", value?.toUpperCase());
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="channel"
                    name="channel"
                    placeholder="Channel Name"
                    onChange={({ target: { value } }) =>
                      formik.setFieldValue("channel", value?.toUpperCase())
                    }
                    onBlur={handleBlur}
                    error={Boolean(touched.channel && errors.channel)}
                    helperText={touched.channel && errors.channel}
                    fullWidth
                    variant="outlined"
                    sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                    InputProps={{ ...params.InputProps, sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Row 4: POS Allowance, POS Allowance Amount, POS Available Allowance, Custom Rate */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px', gridRow: '4' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>POS Allowance</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="radio"
                    name="posAllowance"
                    value="true"
                    checked={formik.values.posAllowance === true}
                    onChange={() => formik.setFieldValue('posAllowance', true)}
                    style={{ accentColor: '#1563FF' }}
                  />
                  <span style={{ fontWeight: 500, color: '#2C3856', fontFamily: "PF DIN Text Pro" }}>True</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="radio"
                    name="posAllowance"
                    value="false"
                    checked={formik.values.posAllowance === false}
                    onChange={() => formik.setFieldValue('posAllowance', false)}
                    style={{ accentColor: '#1563FF' }}
                  />
                  <span style={{ fontWeight: 500, color: '#2C3856', fontFamily: "PF DIN Text Pro" }}>False</span>
                </label>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>POS ALLOWANCE AMOUNT</Typography>
              <TextField
                id="posAllowanceAmount"
                name="posAllowanceAmount"
                placeholder="$0"
                value={formik.values.posAllowanceAmount || ''}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("posAllowanceAmount", value)
                }
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>POS AVAILABLE ALLOWANCE</Typography>
              <TextField
                id="posAvailableAllowance"
                name="posAvailableAllowance"
                disabled
                placeholder="$0"
                value={formik.values.posAvailableAllowance || ''}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("posAvailableAllowance", value)
                }
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>CUSTOM RATE</Typography>
              <TextField
                id="percentageOff"
                name="percentageOff"
                placeholder="0%"
                value={formik.values.percentageOff ?? ''}
                onChange={({ target: { value } }) => {
                  // Handle empty input
                  if (value === '') {
                    formik.setFieldValue("percentageOff", undefined);
                    return;
                  }

                  // Allow valid decimal number format (including trailing dot)
                  if (/^\d*\.?\d*$/.test(value)) {
                    const num = value.endsWith('.') ? value : parseFloat(value);
                    formik.setFieldValue("percentageOff", isNaN(num as number) ? undefined : num);
                  }
                }}
                fullWidth
                variant="outlined"
                sx={{ borderRadius: 2, fontFamily: "PF DIN Text Pro" }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, height: 56, pl: 2 } }}
              />
            </Box>
          </Box>

          {/* Row 5: Notes */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2C3856', mb: 0.5, textTransform: 'uppercase', fontFamily: "PF DIN Text Pro" }}>Notes</Typography>
            <TextField
              id="notes"
              name="notes"
              placeholder="Enter notes here..."
              value={formik.values.notes || ''}
              onChange={({ target: { value } }) =>
                formik.setFieldValue("notes", value)
              }
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              InputProps={{ sx: { borderRadius: 2, fontSize: 18, pl: 2 } }}
            />
          </Box>
          {formik.values.dealerId && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Button
                endIcon={<DeleteOutline sx={{height: 24, width: 24}}/>}
                sx={{
                  color: '#FF3B30',
                  fontWeight: 600,
                  fontFamily: "PF DIN Text Pro",
                  fontSize: 18,
                  pl: 0,
                  mt: 2,
                  mb: 1,
                  alignSelf: 'flex-left',
                  textTransform: 'none',
                  background: 'transparent',
                  boxShadow: 'none',
                  '&:hover': { background: 'transparent', color: '#b71c1c' },
                }}
                onClick={() => onDeleteRequest?.(_dealer!)}
              >
                Delete Attendee
              </Button>
            </Box>
          )}
        </Box>
        {!!errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </DialogContent>
      <DialogActions sx={{
        p: 0,
        m: 0,
        borderRadius: '0 0 10px 10px',
      }}>
        <Button
          onClick={() => onConfirmButtonClick(formik.values as DealerEditFormValues)}
          sx={{
            width: '100%',
            background: '#1563FF',
            color: '#fff',
            fontWeight: 700,
            fontSize: 22,
            borderRadius: '0 0 10px 10px',
            height: 64,
            boxShadow: 1,
            textTransform: 'none',
            m: 0,
            p: 0,
            '&:hover': { background: '#0039CB' },
          }}
        >
          {formik.values.dealerId ? 'Update' : 'Add Attendee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DealerEditDialog;
